import { useState } from "react";
import * as v from "valibot";
import { bounds, Dictionary, dictionaryItems, IntRange, isOfBrand } from "./schemas";

type EntryKeys<V extends v.BaseSchema<any, any, any>> = V extends v.ObjectSchema<infer E, any> ? keyof E : never;
type FocusSchema<V extends v.BaseSchema<any, any, any>, K extends EntryKeys<V>> =
  V extends v.ObjectSchema<infer E, any> ? E[K] : never;

export type UseSchemaApi<S extends v.BaseSchema<any, any, any>> = {
  getSchema(): S;
  focus<K extends EntryKeys<S>>(k: K): UseSchemaApi<FocusSchema<S, K>>;
  value: v.InferOutput<S>;
  setValue(v: v.InferInput<S>): void;
};

const makeFocus =
  <S extends v.BaseSchema<any, any, any>>(schema: S) =>
  <K extends EntryKeys<S>>(key: K): FocusSchema<S, K> => {
    if (schema.type != "object") {
      throw new Error("Cannot focus on non object schema");
    }
    const s = schema as any as v.ObjectSchema<v.ObjectEntries, any>;
    if (!(key in s.entries)) {
      throw new Error("Unknnown key");
    }
    const sub = s.entries[key];
    return sub as any;
  };

export function getSchemaDefaults<S extends v.BaseSchema<any, any, any>>(s: S): v.InferOutput<S> {
  if (isOfBrand(s, Dictionary)) {
    return dictionaryItems(s as any)[0].value;
  }
  // multi dictionary handled by array
  if (isOfBrand(s, IntRange)) {
    const b = bounds(s as any);
    return {
      high: b.max,
      low: b.min,
    };
  }
  if (s.type == "optional") {
    return undefined;
  }
  if (s.type == "array") {
    return [];
  }
  if (s.type == "object") {
    const res = {} as any;
    for (const [k, subschema] of Object.entries((s as any as v.ObjectSchema<v.ObjectEntries, any>).entries)) {
      res[k] = getSchemaDefaults(subschema);
    }
    return res;
  }
  if (s.type == "number") {
    return 0;
  }
  if (s.type == "string") {
    return "";
  }

  // unimplemented
  return null;
}

type KeyPaths<T, Prefix extends string = ""> = {
  [K in keyof T & string]: T[K] extends readonly any[]
    ? `${Prefix}${K}`
    : T[K] extends object
      ? `${Prefix}${K}` | KeyPaths<T[K], `${Prefix}${K}.`>
      : `${Prefix}${K}`;
}[keyof T & string];

export function getIn<T extends object>(v: T, p: KeyPaths<T>) {}

export function useSchema<S extends v.BaseSchema<any, any, any>>(schema: S): UseSchemaApi<S> {
  const [value, setValue] = useState(getSchemaDefaults(schema));
  return {
    focus(x) {
      if (schema.type != "object") {
        throw new Error("Cannot focus on non object schema");
      }
      const s = schema as any as v.ObjectSchema<v.ObjectEntries, any>;
      if (!(x in s.entries)) {
        throw new Error("Unknnown key");
      }
      const sub = s.entries[x];
      return {
        getSchema() {
          return sub;
        },
        focus() {},
      } as any;
    },
    getSchema() {
      return schema;
    },
    setValue,
    value,
  };
}

// type RDict = {
//   [key: string]: string | RDict;
// };

// type Focus<R extends RDict | string> = R extends RDict ? <K extends keyof R>(k: K) => Focus<R[K]> : () => R;
